import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { Textarea } from 'primeng/inputtextarea';
import { DropdownModule } from 'primeng/dropdown';
import { CalendarModule } from 'primeng/calendar';
import { DividerModule } from 'primeng/divider';
import { TooltipModule } from 'primeng/tooltip';
import { AvatarModule } from 'primeng/avatar';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { TableModule } from 'primeng/table';
import { DialogService } from 'primeng/dynamicdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Subject, forkJoin, of } from 'rxjs';
import { takeUntil, finalize, switchMap, catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { TasksService } from '../../../_services/tasks/tasks.service';
import { SitesService } from '../../../_services/sites/sites.service';
import { PatientsService } from '../../../_services/patients/patients.service';
import { AppointmentService } from '../../../_services/appointments/appointment.service';
import { SessionService } from '../../../_services/session/session.service';
import { AppointmentComponent } from '../../../components/appointment/appointment.component';

import {
  ITaskNew,
  ITaskComment,
  TaskPriority,
  TaskStatus,
  IUpdateTask,
  ITaskSubtaskSummary,
} from '../../../_interfaces/ITask';
import IUser from '../../../_interfaces/IUser';
import IPatient from '../../../_interfaces/IPatients';
import IAppointment from '../../../_interfaces/IAppointment';
import ICalendar from '../../../_interfaces/ICalendar';

interface UserOption {
  id: number;
  name: string;
  initials: string;
  color: string;
}

interface PatientOption {
  id: number;
  name: string;
  phone: string;
  email: string;
  initials: string;
}

interface AppointmentOption {
  id: number;
  patientId: number;
  patientName: string;
  date: Date;
  time: string;
  treatment: string;
  status: string;
  total: number;
}

@Component({
  standalone: true,
  selector: 'app-task-detail-real',
  templateUrl: './task-detail-real.component.html',
  styleUrls: ['./task-detail-real.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    InputTextModule,
    Textarea,
    DropdownModule,
    CalendarModule,
    DividerModule,
    TooltipModule,
    AvatarModule,
    ConfirmDialogModule,
    ProgressSpinnerModule,
    ToastModule,
    AutoCompleteModule,
    TableModule,
    TranslateModule,
  ],
  providers: [ConfirmationService, MessageService, DialogService],
})
export class TaskDetailRealComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Loading states
  isLoading: boolean = true;
  isSaving: boolean = false;
  isAddingComment: boolean = false;

  // Context
  customersId: number = 0;
  sitesId: number = 0;
  taskId: number = 0;
  taskKey: string = '';

  // Data
  task: ITaskNew | null = null;
  comments: ITaskComment[] = [];
  users: UserOption[] = [];

  // Modo creación
  isCreateMode: boolean = false;

  isEditing: boolean = false;
  newComment: string = '';

  // Opciones de edición
  priorityOptions: Array<{
    label: string;
    value: TaskPriority;
    icon: string;
    color: string;
  }> = [];
  statusOptions: Array<{ label: string; value: TaskStatus; icon: string }> = [];

  // Campos editables
  editTitle: string = '';
  editDescription: string = '';
  editDueDate: Date = new Date();
  editPriority: TaskPriority = 'medium';
  editStatus: TaskStatus = 'pending';
  selectedUsers: UserOption[] = [];

  // Vinculaciones editables
  selectedPatients: PatientOption[] = [];
  selectedAppointments: AppointmentOption[] = [];
  patientAppointments: AppointmentOption[] = [];

  // Autocomplete y estados
  filteredPatients: PatientOption[] = [];
  filteredAppointments: AppointmentOption[] = [];
  filteredPatientAppointments: AppointmentOption[] = [];
  patientSearchStr: string = '';
  isLoadingPatients: boolean = false;
  isLoadingAppointments: boolean = false;
  isLoadingPatientAppointments: boolean = false;
  showPatientSearch: boolean = false;
  showAppointmentSearch: boolean = false;

  // Rango para búsqueda de citas
  appointmentStartDate: Date = new Date();
  appointmentEndDate: Date = new Date();
  appointmentFilterDate: Date | null = null;

  // Usuario actual para permisos
  currentUserId: number = 0;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private tasksService: TasksService,
    private sitesService: SitesService,
    private patientsService: PatientsService,
    private appointmentService: AppointmentService,
    private session: SessionService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private dialogService: DialogService,
    private translate: TranslateService,
  ) { }

  ngOnInit(): void {
    this.initSelectOptions();
    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.initSelectOptions();
    });

    const customer = this.session.getCustomer();
    const site = this.session.getSite();
    const user = this.session.getUser();
    this.customersId = customer?.id || 0;
    this.sitesId = site?.id || 0;
    this.currentUserId = user?.id || 0;

    // Rango por defecto de búsqueda de citas
    this.appointmentEndDate.setDate(this.appointmentEndDate.getDate() + 7);

    // Modo creación
    const isNewRoute = this.route.snapshot.url.some(
      (seg) => seg.path === 'new',
    );
    if (isNewRoute) {
      this.isCreateMode = true;
      this.isEditing = true;
      this.isLoading = false;
      this.loadUsers();
      this.initCreateFields();
      this.applyPreloadedLinks();
      return;
    }

    const taskKeyParam = this.route.snapshot.paramMap.get('taskKey');
    if (taskKeyParam) {
      this.taskKey = taskKeyParam;
      this.loadData();
    } else {
      this.goBack();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadData(): void {
    this.isLoading = true;

    this.tasksService
      .getTask(this.customersId, this.sitesId, this.taskKey)
      .pipe(
        catchError((error) => {
          const fallbackId = Number(
            this.route.snapshot.queryParamMap.get('taskId'),
          );
          if (fallbackId) {
            return this.tasksService.getTaskById(
              this.customersId,
              this.sitesId,
              fallbackId,
            );
          }
          return throwError(() => error);
        }),
        takeUntil(this.destroy$),
        switchMap((task) => {
          this.task = task;
          this.taskId = task.id || 0;
          const comments$ = this.taskId
            ? this.tasksService.getComments(
              this.customersId,
              this.sitesId,
              this.taskId,
            )
            : of([]);
          return forkJoin({
            comments: comments$,
            users: this.sitesService.getUsers(this.customersId, this.sitesId),
          });
        }),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe({
        next: ({ comments, users }) => {
          this.comments = comments;
          this.users = users.map((u) => this.mapUserToOption(u));
          this.initEditFields();
          const shouldEdit =
            this.route.snapshot.queryParamMap.get('edit') === 'true';
          if (shouldEdit) {
            this.isEditing = true;
          }
        },
        error: (error) => {
          console.error('Error loading task:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('TASKS.MESSAGES.ERROR_TITLE'),
            detail: this.getErrorDetail(error, 'TASKS.MESSAGES.LOAD_ERROR'),
          });
          this.goBack();
        },
      });
  }

  private loadUsers(): void {
    if (!this.customersId || !this.sitesId) return;

    this.sitesService
      .getUsers(this.customersId, this.sitesId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (users: IUser[]) => {
          this.users = users.map((u) => this.mapUserToOption(u));
        },
        error: (error) => {
          console.error('Error loading users:', error);
        },
      });
  }

  private mapUserToOption(user: IUser): UserOption {
    const name = `${user.name} ${user.surname || ''}`.trim();
    const initials =
      user.abbr && user.abbr.trim().length === 2
        ? user.abbr.toUpperCase()
        : this.getInitials(name);
    return {
      id: user.id!,
      name,
      initials,
      color: this.getRandomColor(user.id!),
    };
  }

  private mapPatientToOption(patient: IPatient): PatientOption {
    const name = `${patient.name} ${patient.surname || ''}`.trim();
    return {
      id: patient.id!,
      name,
      phone: patient.phone || '',
      email: patient.email || '',
      initials: this.getInitials(name),
    };
  }

  private mapAppointmentToOption(appointment: IAppointment): AppointmentOption {
    const patientName = this.getPatientNameFromAppointment(appointment);

    const startDate = new Date(appointment.start);
    const time = startDate.toLocaleTimeString(this.getLocale(), {
      hour: '2-digit',
      minute: '2-digit',
    });

    const treatment = appointment.treatments?.length
      ? appointment.treatments[0].treatment?.name ||
      this.translate.instant('TASKS.DETAIL.TREATMENT')
      : this.translate.instant('TASKS.DETAIL.NO_TREATMENT');

    return {
      id: appointment.id!,
      patientId: appointment.patient?.id || appointment.patients_id || 0,
      patientName,
      date: startDate,
      time,
      treatment,
      status:
        appointment.status?.name ||
        this.translate.instant('TASKS.STATUS.PENDING'),
      total: appointment.price || 0,
    };
  }

  private getPatientNameFromAppointment(appointment: IAppointment): string {
    if (appointment.patient) {
      return `${appointment.patient.name} ${appointment.patient.surname || ''}`.trim();
    }

    const patientId = appointment.patient?.id || appointment.patients_id;
    if (patientId) {
      const fromSelected = this.selectedPatients.find(
        (p) => p.id === patientId,
      );
      if (fromSelected) return fromSelected.name;

      const fromTask = this.task?.patients?.find((p) => p.id === patientId);
      if (fromTask) {
        return `${fromTask.name} ${fromTask.surname || ''}`.trim();
      }
    }

    return this.translate.instant('TASKS.DETAIL.NO_PATIENT');
  }

  private initSelectOptions(): void {
    this.priorityOptions = [
      {
        label: this.translate.instant('TASKS.PRIORITY.URGENT'),
        value: 'urgent',
        icon: 'fas fa-exclamation-circle',
        color: '#dc2626',
      },
      {
        label: this.translate.instant('TASKS.PRIORITY.HIGH'),
        value: 'high',
        icon: 'fas fa-arrow-up',
        color: '#ea580c',
      },
      {
        label: this.translate.instant('TASKS.PRIORITY.MEDIUM'),
        value: 'medium',
        icon: 'fas fa-minus',
        color: '#ca8a04',
      },
      {
        label: this.translate.instant('TASKS.PRIORITY.LOW'),
        value: 'low',
        icon: 'fas fa-arrow-down',
        color: '#16a34a',
      },
    ];

    this.statusOptions = [
      {
        label: this.translate.instant('TASKS.STATUS.PENDING'),
        value: 'pending',
        icon: 'fas fa-clock',
      },
      {
        label: this.translate.instant('TASKS.STATUS.IN_PROGRESS'),
        value: 'in-progress',
        icon: 'fas fa-spinner',
      },
      {
        label: this.translate.instant('TASKS.STATUS.COMPLETED'),
        value: 'completed',
        icon: 'fas fa-check-circle',
      },
      {
        label: this.translate.instant('TASKS.STATUS.CANCELLED'),
        value: 'cancelled',
        icon: 'fas fa-ban',
      },
    ];
  }

  private getLocale(): string {
    const lang = this.translate.currentLang || this.translate.defaultLang;
    switch (lang) {
      case 'es':
        return 'es-ES';
      case 'hr':
        return 'hr-HR';
      case 'en':
        return 'en-US';
      default:
        return 'en-US';
    }
  }

  private getInitials(name: string): string {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }

  getUserShortLabel(user?: {
    abbr?: string;
    name?: string;
    surname?: string;
  }): string {
    if (!user) return '??';
    if (user.abbr && user.abbr.trim().length === 2) {
      return user.abbr.toUpperCase();
    }
    const name = `${user.name || ''} ${user.surname || ''}`.trim();
    return this.getInitials(name);
  }

  private getRandomColor(seed: number): string {
    const colors = [
      '#6366f1',
      '#8b5cf6',
      '#ec4899',
      '#f43f5e',
      '#f97316',
      '#eab308',
      '#22c55e',
      '#14b8a6',
      '#06b6d4',
      '#3b82f6',
    ];
    return colors[seed % colors.length];
  }

  // Permisos de tareas
  private isSiteAdmin(): boolean {
    const customer = this.session.getCustomer();
    return customer
      ? this.session.isCustomerAdmin(customer)
      : this.session.isCustomerAdmin();
  }

  /**
   * Verifica si el usuario actual es el creador de la tarea
   * Solo el creador (o superusuario) puede editar/eliminar la tarea
   */
  isTaskCreator(): boolean {
    if (!this.task || !this.currentUserId) return false;
    return (
      this.task.created_by === this.currentUserId ||
      this.isSiteAdmin() ||
      this.session.isSuperUser()
    );
  }

  /**
   * Verifica si el usuario actual puede cambiar el estado de la tarea
   * El creador O el asignado pueden cambiar el estado
   */
  canChangeTaskStatus(): boolean {
    if (!this.task || !this.currentUserId) return false;
    return (
      this.task.created_by === this.currentUserId ||
      this.task.assigned_to === this.currentUserId ||
      (this.task.assignees || []).some((u) => u.id === this.currentUserId) ||
      this.isSiteAdmin() ||
      this.session.isSuperUser()
    );
  }

  canEditTask(): boolean {
    return this.isTaskCreator();
  }

  canCommentTask(): boolean {
    if (!this.task || !this.currentUserId) return false;
    return (
      this.task.created_by === this.currentUserId ||
      this.task.assigned_to === this.currentUserId ||
      (this.task.assignees || []).some((u) => u.id === this.currentUserId) ||
      this.isSiteAdmin() ||
      this.session.isSuperUser()
    );
  }

  initEditFields(): void {
    if (this.task) {
      this.editTitle = this.task.title;
      this.editDescription = this.task.description || '';
      this.editDueDate = new Date(this.task.due_date);
      this.editPriority = this.task.priority;
      this.editStatus = this.task.status;

      if (this.task.assignees && this.task.assignees.length > 0) {
        this.selectedUsers = this.task.assignees.map((u) =>
          this.mapUserToOption(u as IUser),
        );
      } else if (this.task.assigned_to) {
        const user = this.users.find((u) => u.id === this.task!.assigned_to);
        if (user) {
          this.selectedUsers = [user];
        } else if (this.task.assignee) {
          this.selectedUsers = [
            this.mapUserToOption(this.task.assignee as IUser),
          ];
        }
      }

      if (this.task.patients && this.task.patients.length > 0) {
        this.selectedPatients = this.task.patients.map((p) =>
          this.mapPatientToOption(p as IPatient),
        );
        this.showPatientSearch = true;
      }

      if (this.task.appointments && this.task.appointments.length > 0) {
        this.selectedAppointments = this.task.appointments.map((a) =>
          this.mapAppointmentToOption(a as IAppointment),
        );
        this.showAppointmentSearch = true;
      }

      if (this.selectedPatients.length > 0) {
        this.loadAppointmentsForPatients();
      }
    }
  }

  private initCreateFields(): void {
    this.editTitle = '';
    this.editDescription = '';
    this.editDueDate = new Date();
    this.editPriority = 'medium';
    this.editStatus = 'pending';
    this.selectedUsers = [];
    this.selectedPatients = [];
    this.selectedAppointments = [];
    this.patientAppointments = [];
    this.patientSearchStr = '';
  }

  private applyPreloadedLinks(): void {
    const patientId = this.route.snapshot.queryParamMap.get('patientId');
    const appointmentId =
      this.route.snapshot.queryParamMap.get('appointmentId');

    if (patientId && this.customersId) {
      this.patientsService
        .get(this.customersId, Number(patientId))
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (patient) => {
            const option = this.mapPatientToOption(patient);
            this.selectedPatients = [option];
            this.showPatientSearch = true;
            this.loadAppointmentsForPatient(option.id);
          },
          error: (error) =>
            console.error('Error loading preloaded patient:', error),
        });
    }

    if (appointmentId && this.customersId && this.sitesId) {
      this.appointmentService
        .getAppointment(
          this.customersId,
          { id: this.sitesId } as any,
          Number(appointmentId),
        )
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (appointment) => {
            const option = this.mapAppointmentToOption(appointment);
            this.selectedAppointments = [option];
            this.showAppointmentSearch = true;
          },
          error: (error) =>
            console.error('Error loading preloaded appointment:', error),
        });
    }
  }

  toggleEdit(): void {
    this.isEditing = !this.isEditing;
    if (!this.isEditing) {
      this.initEditFields();
    }
  }

  addUser(user: UserOption): void {
    if (!user || this.selectedUsers.some((u) => u.id === user.id)) return;
    this.selectedUsers = [...this.selectedUsers, user];
  }

  removeUser(userId: number): void {
    this.selectedUsers = this.selectedUsers.filter((u) => u.id !== userId);
  }

  isUserSelected(userId: number): boolean {
    return this.selectedUsers.some((u) => u.id === userId);
  }

  searchPatients(event: any): void {
    const query = event.query;
    if (!query || query.length < 2) {
      this.filteredPatients = [];
      return;
    }

    if (!this.customersId || !this.sitesId) return;

    this.isLoadingPatients = true;
    this.patientsService
      .getBySite(this.customersId, this.sitesId, { search: query })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (patients: IPatient[]) => {
          this.filteredPatients = patients.map((p) =>
            this.mapPatientToOption(p),
          );
          this.isLoadingPatients = false;
        },
        error: (error) => {
          console.error('Error searching patients:', error);
          this.filteredPatients = [];
          this.isLoadingPatients = false;
        },
      });
  }

  searchAppointments(): void {
    if (!this.customersId || !this.sitesId) return;

    this.isLoadingAppointments = true;

    const startDate = new Date(this.appointmentStartDate);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(this.appointmentEndDate);
    endDate.setHours(23, 59, 59, 999);
    if (endDate < startDate) {
      endDate.setTime(startDate.getTime());
      endDate.setHours(23, 59, 59, 999);
    }

    const startStr = startDate.toISOString();
    const endStr = endDate.toISOString();

    this.appointmentService
      .getAppointments(this.customersId, { id: this.sitesId } as any, {
        start: startStr,
        end: endStr,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (appointments: IAppointment[]) => {
          this.filteredAppointments = appointments
            .map((a) => this.mapAppointmentToOption(a))
            .filter((a) => !this.isAppointmentSelected(a.id));
          this.isLoadingAppointments = false;
        },
        error: (error) => {
          console.error('Error searching appointments:', error);
          this.isLoadingAppointments = false;
        },
      });
  }

  onSelectPatient(event: any): void {
    const patient = event.value || event;
    if (patient && patient.id) {
      if (!this.selectedPatients.find((p) => p.id === patient.id)) {
        this.selectedPatients = [...this.selectedPatients, patient];
        this.loadAppointmentsForPatient(patient.id);
      }
      this.patientSearchStr = '';
    }
  }

  onSelectAppointment(event: any): void {
    const appointment = event.value || event;
    if (appointment && appointment.id) {
      if (!this.selectedAppointments.find((a) => a.id === appointment.id)) {
        this.selectedAppointments = [...this.selectedAppointments, appointment];
        this.patientAppointments = this.patientAppointments.filter(
          (apt) => apt.id !== appointment.id,
        );
      }
    }
  }

  loadAppointmentsForPatient(patientId: number): void {
    if (!this.customersId || !this.sitesId) return;

    this.isLoadingPatientAppointments = true;
    this.appointmentService
      .getByPatient(this.customersId, patientId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (appointments: IAppointment[]) => {
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          const futureAppointments = appointments.filter((a) => {
            const appointmentDate = new Date(a.start);
            return appointmentDate >= today;
          });

          const newAppointments = futureAppointments
            .map((a) => this.mapAppointmentToOption(a))
            .filter((apt) => !this.isAppointmentSelected(apt.id));

          newAppointments.forEach((apt) => {
            if (!this.patientAppointments.find((a) => a.id === apt.id)) {
              this.patientAppointments.push(apt);
            }
          });

          this.updateFilteredPatientAppointments();
          this.isLoadingPatientAppointments = false;
          if (this.patientAppointments.length > 0) {
            this.showAppointmentSearch = true;
          }
        },
        error: (error) => {
          console.error('Error loading patient appointments:', error);
          this.isLoadingPatientAppointments = false;
        },
      });
  }

  loadAppointmentsForPatients(): void {
    this.patientAppointments = [];
    this.selectedPatients.forEach((patient) => {
      this.loadAppointmentsForPatient(patient.id);
    });
  }

  onAppointmentFilterDateChange(): void {
    this.updateFilteredPatientAppointments();
  }

  clearAppointmentFilterDate(): void {
    this.appointmentFilterDate = null;
    this.updateFilteredPatientAppointments();
  }

  private updateFilteredPatientAppointments(): void {
    if (!this.appointmentFilterDate) {
      this.filteredPatientAppointments = [...this.patientAppointments];
      return;
    }

    const target = new Date(this.appointmentFilterDate);
    target.setHours(0, 0, 0, 0);

    this.filteredPatientAppointments = this.patientAppointments.filter(
      (apt) => {
        const date = new Date(apt.date);
        date.setHours(0, 0, 0, 0);
        return date.getTime() === target.getTime();
      },
    );
  }

  addPatientAppointment(appointment: AppointmentOption): void {
    if (!appointment || this.isAppointmentSelected(appointment.id)) return;
    this.selectedAppointments = [...this.selectedAppointments, appointment];
    this.patientAppointments = this.patientAppointments.filter(
      (apt) => apt.id !== appointment.id,
    );
    this.updateFilteredPatientAppointments();
  }

  removePatient(patientId: number): void {
    this.selectedPatients = this.selectedPatients.filter(
      (p) => p.id !== patientId,
    );
    this.patientAppointments = this.patientAppointments.filter((apt) =>
      this.selectedPatients.some((p) => p.id === apt.patientId),
    );
    this.updateFilteredPatientAppointments();
  }

  removeAppointment(appointmentId: number): void {
    this.selectedAppointments = this.selectedAppointments.filter(
      (a) => a.id !== appointmentId,
    );
    if (this.selectedPatients.length > 0) {
      this.loadAppointmentsForPatients();
    }
  }

  isAppointmentSelected(appointmentId: number): boolean {
    return this.selectedAppointments.some((a) => a.id === appointmentId);
  }

  togglePatientSearch(): void {
    this.showPatientSearch = !this.showPatientSearch;
  }

  toggleAppointmentSearch(): void {
    this.showAppointmentSearch = !this.showAppointmentSearch;
    if (this.showAppointmentSearch) {
      this.searchAppointments();
    }
  }

  formatAppointmentDate(date: Date): string {
    return date.toLocaleDateString('es-ES', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
    });
  }

  saveChanges(): void {
    const title = this.editTitle.trim();
    if (!title) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('TASKS.MESSAGES.ERROR_TITLE'),
        detail: this.translate.instant('TASKS.MESSAGES.VALIDATION_TITLE'),
      });
      return;
    }

    if (this.selectedUsers.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('TASKS.MESSAGES.ERROR_TITLE'),
        detail: this.translate.instant('TASKS.MESSAGES.VALIDATION_ASSIGNEE'),
      });
      return;
    }

    this.isSaving = true;

    const payloadBase = {
      title,
      description: this.editDescription.trim() || undefined,
      due_date: this.editDueDate.toISOString(),
      priority: this.editPriority,
      status: this.editStatus,
      assigned_to_ids: this.selectedUsers.map((u) => u.id),
      patients_ids: this.selectedPatients.map((p) => p.id),
      appointments_ids: this.selectedAppointments.map((a) => a.id),
    };

    if (this.isCreateMode) {
      this.tasksService
        .createTask(this.customersId, this.sitesId, payloadBase)
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => (this.isSaving = false)),
        )
        .subscribe({
          next: (createdTask) => {
            this.task = createdTask;
            this.taskId = createdTask.id || 0;
            this.isCreateMode = false;
            this.isEditing = false;
            this.router.navigate([
              `/${this.session.getCustomer()?.identifier}/${this.session.getSite()?.identifier}/tasks`,
              createdTask.task_key,
            ]);
            this.messageService.add({
              severity: 'success',
              summary: this.translate.instant('TASKS.MESSAGES.SUCCESS_TITLE'),
              detail: this.translate.instant('TASKS.MESSAGES.CREATE_SUCCESS'),
            });
          },
          error: (error) => {
            console.error('Error creating task:', error);
            this.messageService.add({
              severity: 'error',
              summary: this.translate.instant('TASKS.MESSAGES.ERROR_TITLE'),
              detail: this.getErrorDetail(error, 'TASKS.MESSAGES.CREATE_ERROR'),
            });
          },
        });
      return;
    }

    if (!this.task) return;

    const updateData: IUpdateTask = payloadBase;

    this.tasksService
      .updateTask(this.customersId, this.sitesId, this.taskId, updateData)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isSaving = false)),
      )
      .subscribe({
        next: (updatedTask) => {
          this.task = updatedTask;
          this.isEditing = false;
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('TASKS.MESSAGES.SUCCESS_TITLE'),
            detail: this.translate.instant('TASKS.MESSAGES.UPDATE_SUCCESS'),
          });
        },
        error: (error) => {
          console.error('Error updating task:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('TASKS.MESSAGES.ERROR_TITLE'),
            detail: this.getErrorDetail(error, 'TASKS.MESSAGES.UPDATE_ERROR'),
          });
        },
      });
  }

  addComment(): void {
    if (!this.task || !this.newComment.trim() || !this.canCommentTask()) {
      return;
    }

    this.isAddingComment = true;

    const commentContent = this.newComment.trim();

    this.tasksService
      .createComment(
        this.customersId,
        this.sitesId,
        this.taskId,
        commentContent,
      )
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isAddingComment = false)),
      )
      .subscribe({
        next: (newCommentObj) => {
          this.comments.unshift(newCommentObj);
          this.newComment = '';
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('TASKS.MESSAGES.SUCCESS_TITLE'),
            detail: this.translate.instant(
              'TASKS.MESSAGES.COMMENT_ADD_SUCCESS',
            ),
          });
        },
        error: (error) => {
          console.error('Error adding comment:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('TASKS.MESSAGES.ERROR_TITLE'),
            detail: this.getErrorDetail(
              error,
              'TASKS.MESSAGES.COMMENT_ADD_ERROR',
            ),
          });
        },
      });
  }

  deleteComment(commentId: number): void {
    this.confirmationService.confirm({
      message: this.translate.instant('TASKS.CONFIRM.DELETE_COMMENT.MESSAGE'),
      header: this.translate.instant('TASKS.CONFIRM.DELETE_COMMENT.TITLE'),
      icon: 'fas fa-exclamation-triangle',
      accept: () => {
        this.tasksService
          .deleteComment(this.customersId, this.sitesId, this.taskId, commentId)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.comments = this.comments.filter((c) => c.id !== commentId);
              this.messageService.add({
                severity: 'success',
                summary: this.translate.instant('TASKS.MESSAGES.SUCCESS_TITLE'),
                detail: this.translate.instant(
                  'TASKS.MESSAGES.COMMENT_DELETE_SUCCESS',
                ),
              });
            },
            error: (error) => {
              console.error('Error deleting comment:', error);
              this.messageService.add({
                severity: 'error',
                summary: this.translate.instant('TASKS.MESSAGES.ERROR_TITLE'),
                detail: this.getErrorDetail(
                  error,
                  'TASKS.MESSAGES.COMMENT_DELETE_ERROR',
                ),
              });
            },
          });
      },
    });
  }

  markAsCompleted(): void {
    if (!this.task) return;

    this.tasksService
      .updateTaskStatus(
        this.customersId,
        this.sitesId,
        this.taskId,
        'completed',
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updatedTask) => {
          this.task = updatedTask;
          this.editStatus = 'completed';
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('TASKS.MESSAGES.SUCCESS_TITLE'),
            detail: this.translate.instant('TASKS.MESSAGES.STATUS_COMPLETED'),
          });
        },
        error: (error) => {
          console.error('Error completing task:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('TASKS.MESSAGES.ERROR_TITLE'),
            detail: this.getErrorDetail(
              error,
              'TASKS.MESSAGES.STATUS_UPDATE_ERROR',
            ),
          });
        },
      });
  }

  reopenTask(): void {
    if (!this.task) return;

    this.tasksService
      .updateTaskStatus(this.customersId, this.sitesId, this.taskId, 'pending')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updatedTask) => {
          this.task = updatedTask;
          this.editStatus = 'pending';
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('TASKS.MESSAGES.SUCCESS_TITLE'),
            detail: this.translate.instant('TASKS.MESSAGES.STATUS_REOPENED'),
          });
        },
        error: (error) => {
          console.error('Error reopening task:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('TASKS.MESSAGES.ERROR_TITLE'),
            detail: this.getErrorDetail(error, 'TASKS.MESSAGES.REOPEN_ERROR'),
          });
        },
      });
  }

  deleteTask(): void {
    this.confirmationService.confirm({
      message: this.translate.instant('TASKS.CONFIRM.DELETE.MESSAGE'),
      header: this.translate.instant('TASKS.CONFIRM.DELETE.TITLE'),
      icon: 'fas fa-exclamation-triangle',
      accept: () => {
        this.tasksService
          .deleteTask(this.customersId, this.sitesId, this.taskId)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.messageService.add({
                severity: 'success',
                summary: this.translate.instant('TASKS.MESSAGES.SUCCESS_TITLE'),
                detail: this.translate.instant('TASKS.MESSAGES.DELETE_SUCCESS'),
              });
              this.goBack();
            },
            error: (error) => {
              console.error('Error deleting task:', error);
              this.messageService.add({
                severity: 'error',
                summary: this.translate.instant('TASKS.MESSAGES.ERROR_TITLE'),
                detail: this.getErrorDetail(
                  error,
                  'TASKS.MESSAGES.DELETE_ERROR',
                ),
              });
            },
          });
      },
    });
  }

  openPatient(patientId: number): void {
    const customer = this.session.getCustomer();
    if (!customer || !patientId) return;
    this.router.navigate([
      `/${customer.identifier}/patients/${patientId}/details`,
    ]);
  }

  openAppointment(appointmentId: number, calendar?: ICalendar): void {
    if (!appointmentId) return;
    this.dialogService.open(AppointmentComponent, {
      closable: true,
      modal: true,
      data: {
        calendar,
        appointments_id: appointmentId,
      },
      header: this.translate.instant('TASKS.APPOINTMENTS.DIALOG_TITLE'),
      width: '70%',
    });
  }

  private translateCode(code?: string): string {
    if (!code) return '';
    const key = `TASKS.CODES.${code}`;
    const translated = this.translate.instant(key);
    return translated === key ? code : translated;
  }

  private getErrorDetail(error: any, fallbackKey: string): string {
    const code = error?.error?.code || error?.error?.message;
    const translated = this.translateCode(code);
    if (translated) return translated;
    return this.translate.instant(fallbackKey);
  }

  openTaskByKey(taskKey: string, taskId?: number): void {
    const customer = this.session.getCustomer();
    const site = this.session.getSite();
    if (!customer || !site || !taskKey) return;
    this.router
      .navigate([`/${customer.identifier}/${site.identifier}/tasks`, taskKey], {
        queryParams: taskId ? { taskId } : {},
      })
      .then(() => {
        this.isCreateMode = false;
        this.isEditing = false;
        this.taskKey = taskKey;
        if (taskId) {
          this.taskId = taskId;
        }
        this.loadData();
      });
  }

  goBack(): void {
    const customer = this.session.getCustomer();
    const site = this.session.getSite();
    if (customer && site) {
      this.router.navigate([
        `/${customer.identifier}/${site.identifier}/tasks`,
      ]);
    } else {
      this.router.navigate(['/']);
    }
  }

  getPriorityLabel(priority: TaskPriority): string {
    const labels: Record<TaskPriority, string> = {
      urgent: this.translate.instant('TASKS.PRIORITY.URGENT'),
      high: this.translate.instant('TASKS.PRIORITY.HIGH'),
      medium: this.translate.instant('TASKS.PRIORITY.MEDIUM'),
      low: this.translate.instant('TASKS.PRIORITY.LOW'),
    };
    return labels[priority];
  }

  getStatusLabel(status: TaskStatus): string {
    const labels: Record<TaskStatus, string> = {
      pending: this.translate.instant('TASKS.STATUS.PENDING'),
      'in-progress': this.translate.instant('TASKS.STATUS.IN_PROGRESS'),
      completed: this.translate.instant('TASKS.STATUS.COMPLETED'),
      cancelled: this.translate.instant('TASKS.STATUS.CANCELLED'),
    };
    return labels[status];
  }

  getAssigneeName(): string {
    const options = this.getAssigneeOptions();
    if (options.length === 0)
      return this.translate.instant('TASKS.UI.ASSIGNEE.NONE');
    if (options.length === 1) return options[0].name;
    return `${options[0].name} (+${options.length - 1})`;
  }

  getAssigneeInitials(): string {
    const options = this.getAssigneeOptions();
    return options.length > 0 ? options[0].initials : '??';
  }

  getAssigneeColor(): string {
    const options = this.getAssigneeOptions();
    return options.length > 0 ? options[0].color : '#6366f1';
  }

  getAssigneeAvatarColor(userId: number): string {
    return this.getRandomColor(userId);
  }

  private getAssigneeOptions(): UserOption[] {
    if (!this.task) return [];
    const assignees = this.task.assignees || [];
    if (assignees.length > 0) {
      return assignees.map((u) => this.mapUserToOption(u as IUser));
    }

    if (this.task.assigned_to_ids && this.task.assigned_to_ids.length > 0) {
      return this.task.assigned_to_ids
        .map((id) => this.users.find((u) => u.id === id))
        .filter(Boolean) as UserOption[];
    }

    if (this.task.assignee) {
      return [this.mapUserToOption(this.task.assignee as IUser)];
    }

    if (this.task.assigned_to) {
      const user = this.users.find((u) => u.id === this.task!.assigned_to);
      return user ? [user] : [];
    }

    return [];
  }

  getCreatorName(): string {
    if (this.task?.creator) {
      const user = this.task.creator;
      return `${user.name} ${user.surname || ''}`.trim();
    }
    return 'Sistema';
  }

  getCreatorInitials(): string {
    if (this.task?.creator) {
      return this.getUserShortLabel(this.task.creator);
    }
    return 'SY';
  }

  getCreatorColor(): string {
    if (this.task?.creator?.id) {
      return this.getRandomColor(this.task.creator.id);
    }
    return '#6366f1';
  }

  getCommentUserName(comment: ITaskComment): string {
    if (comment.user) {
      return `${comment.user.name} ${comment.user.surname || ''}`.trim();
    }
    return 'Usuario';
  }

  getCommentUserInitials(comment: ITaskComment): string {
    return this.getUserShortLabel(comment.user);
  }

  getCommentUserColor(comment: ITaskComment): string {
    if (comment.user?.id) {
      return this.getRandomColor(comment.user.id);
    }
    return '#6366f1';
  }

  formatDate(date: Date | string): string {
    return new Date(date).toLocaleDateString(this.getLocale(), {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }

  formatDateTime(date: Date | string): string {
    return new Date(date).toLocaleDateString(this.getLocale(), {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  getTimeAgo(date: Date | string): string {
    const now = new Date();
    const diff = now.getTime() - new Date(date).getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) {
      return this.translate.instant('TASKS.DETAIL.TIME_AGO.JUST_NOW');
    }
    if (minutes < 60) {
      return this.translate.instant('TASKS.DETAIL.TIME_AGO.MINUTES', {
        count: minutes,
      });
    }
    if (hours < 24) {
      return this.translate.instant('TASKS.DETAIL.TIME_AGO.HOURS', {
        count: hours,
      });
    }
    if (days === 1) {
      return this.translate.instant('TASKS.DETAIL.TIME_AGO.YESTERDAY');
    }
    return this.translate.instant('TASKS.DETAIL.TIME_AGO.DAYS', {
      count: days,
    });
  }

  /**
   * Obtiene el nombre del primer paciente vinculado (o indica si hay más)
   */
  getPatientName(): string {
    if (this.task?.patients && this.task.patients.length > 0) {
      const patient = this.task.patients[0];
      const name = `${patient.name} ${patient.surname || ''}`.trim();
      if (this.task.patients.length > 1) {
        return `${name} (+${this.task.patients.length - 1})`;
      }
      return name;
    }
    return '';
  }

  /**
   * Obtiene información de la primera cita vinculada (o indica si hay más)
   */
  getAppointmentInfo(): string {
    if (this.task?.appointments && this.task.appointments.length > 0) {
      const apt = this.task.appointments[0];
      const appointmentDate = new Date(apt.start);
      const formattedDate = appointmentDate.toLocaleDateString(
        this.getLocale(),
        {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        },
      );
      const formattedTime = appointmentDate.toLocaleTimeString(
        this.getLocale(),
        {
          hour: '2-digit',
          minute: '2-digit',
        },
      );
      const appointmentName =
        apt.name || this.translate.instant('TASKS.APPOINTMENTS.DEFAULT_NAME');
      let info = `${appointmentName} - ${formattedDate} ${formattedTime}`;
      if (this.task.appointments.length > 1) {
        info += ` (+${this.task.appointments.length - 1})`;
      }
      return info;
    }
    return '';
  }
}
